import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ShieldAlert, Search, AlertTriangle } from "lucide-react";

const TYPE_LABEL = { yellow: "🟨 Amm.", red_direct: "🟥 Rosso diretto", red_double: "🟥 Doppia amm." };

export default function AdminDisciplinare() {
    const [divisions, setDivisions] = useState([]);
    const [cups, setCups] = useState([]);
    const [comp, setComp] = useState("");
    const [q, setQ] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [cleanMsg, setCleanMsg] = useState(null);
    const [cleanPreview, setCleanPreview] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [d, c] = await Promise.all([api.get("/divisions"), api.get("/cups")]);
                setDivisions((d.data.divisions || d.data || []));
                setCups((c.data.cups || c.data || []));
            } catch (e) { /* silent */ }
        })();
    }, []);

    const runAudit = async () => {
        if (!comp) { setError("Seleziona una competizione"); return; }
        setLoading(true); setError(null); setData(null);
        try {
            const { data } = await api.get(`/admin/disciplinary/audit`, { params: { comp, q: q || undefined } });
            setData(data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const cleanup = async (dry) => {
        setCleanMsg(null); setError(null);
        const scope = comp ? `la competizione selezionata (${comp})` : "TUTTE le competizioni";
        if (!dry && !confirm(`Rimuovere i gialli ridondanti (2 gialli + doppia amm. nella stessa partita) in ${scope}? L'operazione modifica i tabellini.`)) return;
        try {
            const { data } = await api.post(`/admin/disciplinary/cleanup-redundant-yellows`, null, { params: { dry_run: dry, comp: comp || undefined } });
            if (dry) {
                setCleanPreview(data);
                setCleanMsg(`Anteprima: ${data.removed_total} gialli ridondanti in ${data.details.length} partite.`);
            } else {
                setCleanPreview(null);
                setCleanMsg(`Fatto: rimossi ${data.removed_total} gialli ridondanti. Ricontrolla l'audit.`);
                if (comp) runAudit();
            }
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <div className="max-w-4xl" data-testid="admin-disciplinare">
            <div className="flex items-center gap-3 mb-2">
                <ShieldAlert className="w-6 h-6 text-tff7a1a" />
                <h1 className="op-heading text-tffffff text-2xl">DIAGNOSI DISCIPLINARE</h1>
            </div>
            <p className="op-mono text-xs text-t8f95a3 mb-6 leading-relaxed max-w-2xl">
                Strumento di sola lettura: elenca tutti i cartellini di ogni giocatore in una competizione
                e le giornate di squalifica calcolate. Segnala le anomalie (2 gialli + doppia ammonizione
                nella stessa partita = doppio conteggio) e permette di ripulirle.
            </p>

            <div className="op-card p-5 mb-6 space-y-3" data-testid="disc-audit-form">
                <div className="flex flex-col sm:flex-row gap-3">
                    <select value={comp} onChange={(e) => setComp(e.target.value)}
                        className="flex-1 bg-t0e1014 border border-t22252a text-tffffff text-sm px-2 py-2 focus:border-tff7a1a focus:outline-none"
                        data-testid="disc-comp-select">
                        <option value="">-- competizione --</option>
                        <optgroup label="Campionati">
                            {divisions.map((d) => <option key={d.code || d.id} value={d.code}>{d.code} — {d.name}</option>)}
                        </optgroup>
                        <optgroup label="Coppe">
                            {cups.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                        </optgroup>
                    </select>
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtra giocatore (nome)"
                        className="flex-1 bg-t0e1014 border border-t22252a text-tffffff text-sm px-2 py-2 focus:border-tff7a1a focus:outline-none"
                        data-testid="disc-q-input" />
                    <button onClick={runAudit} disabled={loading} className="op-btn op-btn-primary text-sm flex items-center gap-2 disabled:opacity-40" data-testid="disc-audit-btn">
                        <Search className="w-4 h-4" /> {loading ? "Cerco…" : "Analizza"}
                    </button>
                </div>
                <div className="flex flex-wrap gap-2 pt-2 border-t border-t22252a">
                    <button onClick={() => cleanup(true)} className="op-btn op-btn-ghost text-xs" data-testid="disc-cleanup-preview">Anteprima pulizia gialli ridondanti</button>
                    <button onClick={() => cleanup(false)} className="op-btn op-btn-ghost text-xs text-tff4d4d" data-testid="disc-cleanup-apply">Applica pulizia</button>
                </div>
            </div>

            {error && <div className="op-card p-3 mb-4 text-tff4d4d text-sm" data-testid="disc-error">{error}</div>}
            {cleanMsg && <div className="op-card p-3 mb-4 text-t7ce38b text-sm" data-testid="disc-clean-msg">{cleanMsg}</div>}
            {cleanPreview?.details?.length > 0 && (
                <div className="op-card p-3 mb-4" data-testid="disc-clean-preview">
                    <div className="op-label text-[10px] text-t8f95a3 mb-2">PARTITE INTERESSATE</div>
                    {cleanPreview.details.map((d, i) => (
                        <div key={i} className="op-mono text-xs text-tc9ccd3">{d.comp} {d.giornata ? `· G${d.giornata}` : d.round ? `· ${d.round}` : ""} — rimuove {d.removed} giallo/i</div>
                    ))}
                </div>
            )}

            {data && (
                <div data-testid="disc-results">
                    {data.anomalies.length > 0 && (
                        <div className="op-card p-4 mb-4 border-l-2 border-tff4d4d" data-testid="disc-anomalies">
                            <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="w-4 h-4 text-tff4d4d" />
                                <span className="op-label text-xs text-tff4d4d">ANOMALIE ({data.anomalies.length})</span>
                            </div>
                            {data.anomalies.map((a, i) => (
                                <div key={i} className="op-mono text-xs text-tc9ccd3">
                                    <b>{a.name || a.pes_id}</b> — {a.label}: {a.redundant_yellows} giallo/i + doppia amm. (doppio conteggio)
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="op-label text-[10px] text-t8f95a3 mb-2">{data.competition} — {data.players.length} giocatori con cartellini</div>
                    <div className="space-y-2">
                        {data.players.map((p) => (
                            <div key={p.pes_id} className="op-card p-3" data-testid={`disc-player-${p.pes_id}`}>
                                <div className="flex items-center justify-between gap-3 mb-1">
                                    <div>
                                        <span className="text-tffffff text-sm font-semibold">{p.name || p.pes_id}</span>
                                        <span className="op-mono text-[10px] text-t5c6370 ml-2">{p.team || ""}</span>
                                    </div>
                                    {p.suspended_for_next
                                        ? <span className="op-label text-[9px] text-tff4d4d border border-tff4d4d/50 px-1.5 py-0.5">SQUALIFICATO · {p.suspended_remaining} giornata/e</span>
                                        : <span className="op-label text-[9px] text-t7ce38b border border-t7ce38b/40 px-1.5 py-0.5">a posto</span>}
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {p.cards.map((c, i) => (
                                        <span key={i} className="op-mono text-[10px] text-tc9ccd3 bg-t0e1014 border border-t22252a px-1.5 py-0.5">
                                            {c.label}: {TYPE_LABEL[c.type] || c.type}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
